import { IDBEntityCacheStore } from './IDBEntityCacheStore'
import { Timeouts } from '@gap/utils/date-utils'
import { apiClient } from 'api/trpc/trpc-client'
import { FYIDto } from '@gap/backend/app-services/fyi/models/FYIDto'

const config = {
   ttlMs: Timeouts.minute * 2, // How long this is valid for
   concurrentPageRequests: 3, // How many pages to request at once
}

export interface IDBFYIClientsCacheParams {
   orgId: string
   /** XPM client uuid sent with the first page so the matching FYI client is in the first batch. */
   xeroId?: string
   /** Receives every client loaded so far each time a batch of pages lands. */
   onClientsLoaded?: (clients: FYIDto.clientMinimal[]) => void
}

interface FetchClientPagesParams extends Pick<IDBFYIClientsCacheParams, 'onClientsLoaded'> {
   clients: FYIDto.clientMinimal[]
   fromPage: number
   totalPages: number
}

export class IDBFYIClientsCache {
   /** Cache-or-network: returns the org's full FYI client list if fresh, otherwise pages it in and caches it once complete. */
   static get = async (params: IDBFYIClientsCacheParams): Promise<FYIDto.clientMinimal[] | null> => {
      return await IDBFYIClientsCache.cache(params).fetch()
   }

   static invalidate = async (params: Pick<IDBFYIClientsCacheParams, 'orgId'>): Promise<void> => {
      await IDBFYIClientsCache.cache(params).invalidate()
   }

   private static cache = (params: IDBFYIClientsCacheParams) => {
      return new IDBEntityCacheStore<FYIDto.clientMinimal[]>({
         entityKey: `fyi-clients-${params.orgId}`,
         fetcher: () => IDBFYIClientsCache.fetcher(params),
         cacheOptions: { ttlMs: config.ttlMs },
      })
   }

   /** Loads page one with the uuid lookup, then the remaining pages, resolving only once every page is in. */
   private static fetcher = async (params: IDBFYIClientsCacheParams): Promise<FYIDto.clientMinimal[]> => {
      const firstPage = await apiClient.fyi.getClients.query({ page: 1, xeroId: params.xeroId })
      const firstPageClients = firstPage.matchedClient ? [firstPage.matchedClient, ...firstPage.clients] : firstPage.clients
      const clients = IDBFYIClientsCache.mergeClients({ into: [], clients: firstPageClients })
      params.onClientsLoaded?.(clients)
      return await IDBFYIClientsCache.fetchClientPages({ clients, fromPage: 2, totalPages: firstPage.totalPages, onClientsLoaded: params.onClientsLoaded })
   }

   private static fetchClientPages = async (params: FetchClientPagesParams): Promise<FYIDto.clientMinimal[]> => {
      if (params.fromPage > params.totalPages) {
         return params.clients
      }
      const lastPageInBatch = Math.min(params.fromPage + config.concurrentPageRequests - 1, params.totalPages)
      const pageNumbers = Array.from({ length: lastPageInBatch - params.fromPage + 1 }, (_unused, index) => params.fromPage + index)
      const pages = await Promise.all(pageNumbers.map(page => apiClient.fyi.getClients.query({ page })))
      const clients = IDBFYIClientsCache.mergeClients({ into: params.clients, clients: pages.flatMap(page => page.clients) })
      params.onClientsLoaded?.(clients)
      return await IDBFYIClientsCache.fetchClientPages({ ...params, clients, fromPage: lastPageInBatch + 1 })
   }

   /** Appends the clients not yet present, keeping the first occurrence of each id. */
   private static mergeClients = (params: { into: FYIDto.clientMinimal[], clients: FYIDto.clientMinimal[] }): FYIDto.clientMinimal[] => {
      const clientsById = new Map(params.into.map(client => [client.id, client]))
      for (const client of params.clients) {
         if (!clientsById.has(client.id)) {
            clientsById.set(client.id, client)
         }
      }
      return [...clientsById.values()]
   }
}
